// @flow

/**
 * password_strength_plugin.js
 * Copyright (c) 20010 myPocket technologies (www.mypocket-technologies.com)
 *
 * Modified to remove jquery usage by Michael Probber (michaelp)
 * Extracted scoring to separate function (mjennings)
 *
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:

 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 * @author Darren Mason (djmason9@gmail.com)
 * @date 3/13/2009
 * @projectDescription Password Strength Meter is a jQuery plug-in provide you smart
 * algorithm to detect a password strength.
 *
 * Based on Firas Kassem orginal plugin
 * http://phiras.wordpress.com/2007/04/08/password-strength-meter-a-jquery-plugin/
 *
 * @version 1.0.1
 *
 *
*/

function uniqueCharacters(str) {
  const charSet = {};
  let size = 0;
  for (let i = 0; i < str.length; i += 1) {
    if (!(str.charAt(i) in charSet)) {
      charSet[str.charAt(i)] = true;
      size += 1;
    }
  }
  return size;
}

const evaluationCriteria = {
  has3numbers: (password) => password.match(/(.*[0-9].*[0-9].*[0-9])/),
  has2symbols: (password) =>
    password.match(/(.*[!,@,#,$,%,\^,&,*,?,_,~].*[!,@,#,$,%,\^,&,*,?,_,~])/),
  hasUpperAndLowerCase: (password) => password.match(/([a-z].*[A-Z])|([A-Z].*[a-z])/),
  hasNumsAndChars: (password) => password.match(/([a-zA-Z])/) && password.match(/([0-9])/),
  hasNumsAndSymbols: (password) =>
    password.match(/([!,@,#,$,%,\^,&,*,?,_,~])/) && password.match(/([0-9])/),
  hasCharsAndSymbols: (password) =>
    password.match(/([!,@,#,$,%,\^,&,*,?,_,~])/) && password.match(/([a-zA-Z])/),
  justNumsOrSingleCase: (password) =>
    password.match(/^[a-z]+$/) || password.match(/^[A-Z]+$/) || password.match(/^\d+$/),
};

export function scoreAndSuggest(password: string) {
  const standardPoints = 15;
  const rulesAndPoints = {
    justNumsOrSingleCase: -10,
    hasUpperAndLowerCase: 10,
    hasNumsAndChars: standardPoints,
    hasCharsAndSymbols: standardPoints,
    hasNumsAndSymbols: standardPoints,
    has3numbers: standardPoints,
    has2symbols: standardPoints,
  };

  const score = Object.keys(rulesAndPoints).reduce((memo, key) => {
    const passesTest = evaluationCriteria[key](password);
    const assignedPoints = rulesAndPoints[key];
    const points = passesTest ? assignedPoints : 0;

    return memo + points;
  }, 0);

  return {
    score: score + password.length * 2 + uniqueCharacters(password) * 3,
  };
}

/*
* ==============================================
* MAIN UTILITY
* ==============================================
*/

type TestStrengthOptions = {|
  shortPassStr: string,
  badPassStyle: string,
  samePasswordStr: string,
  blackPassStr: string,
  goodPassStr: string,
  userid: string,
  badPassStr?: string,
  goodPassStyle?: string,
|};

export default function testStrength(
  password: ?string,
  option: TestStrengthOptions,
  weakPasswords: Array<string>,
) {
  const passwordScore = scoreAndSuggest(password || '');
  const { score } = passwordScore;
  let overridePasswordScore = {
    // Previously this was: fieldInfo: [option.strongPassStr, option.strongPassStyle, 0],
    // but no usages of testStrength passed these options
    fieldInfo: ['', '', 0],
  };

  // Styles, the second entry in the fieldInfo array, do not appear to be used anywhere

  if (!password) {
    overridePasswordScore = {
      fieldInfo: [],
      score: 0,
    };
  } else if (password.length < 6) {
    // password < 6
    overridePasswordScore = {
      fieldInfo: [option.shortPassStr, option.badPassStyle, 1],
      score: 0,
    };
  } else if (option.userid.length > 0 && password.toLowerCase() === option.userid.toLowerCase()) {
    // password === user name
    overridePasswordScore = {
      fieldInfo: [option.samePasswordStr, option.badPassStyle, 1],
      score: 0,
    };
  } else if (weakPasswords.indexOf(password.toLowerCase()) > -1) {
    // password in weakPasswords
    overridePasswordScore = {
      fieldInfo: [option.blackPassStr, option.badPassStyle, 1],
      score: 0,
    };
  } else if (score < 34) {
    overridePasswordScore = {
      fieldInfo: [option.badPassStr, option.badPassStyle, 0],
    };
  } else if (score < 68) {
    overridePasswordScore = {
      fieldInfo: [option.goodPassStr, option.goodPassStyle, 0],
    };
  }

  return { ...passwordScore, ...overridePasswordScore };
}
